import React, { useEffect, useState } from "react";
import type { SelectProps } from "antd";
import { Input, Form, Button, Divider, Select, message, Modal, Row, Col, InputNumber, DatePicker } from "antd";
import styles from "../maintenance.module.scss";
import { useTranslation } from "react-i18next";
import { CharacterVerify } from "../../../Utility/RegExp";
import type { RangePickerProps } from "antd/es/date-picker";
import moment from "moment";

import { useCreateTicket } from "../queries";
import { useIdentity } from "Components/Topology/queries";
import { useUser } from "../../Management/queries";
import { useAppSelector } from "store/hook";

const { Option } = Select;

interface createInfoProps {
  CreateVisible: boolean;
  setCreateVisible: (arg: boolean) => void;
}

// Select Option Function
export const selectOption = (element: any) => {
  interface ItemProps {
    label: string;
    value: string;
  }

  const options_raw: any = [];
  const options: ItemProps[] = [];

  // deduplication
  element?.map((item: any) => {
    if (options_raw.indexOf(item.nodeInf.id) === -1) {
      options_raw.push(item.nodeInf.id);
    }
  });

  // sorting
  options_raw?.sort().map((item: any, index: number) => {
    options.push({
      label: item,
      value: item,
    });
  });

  return options;
};

const CreateTicket_Modal = React.memo(({ CreateVisible, setCreateVisible }: createInfoProps) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [ticketType, setTicketType] = useState("");
  const { mutateAsync: createTicketMutation, isLoading } = useCreateTicket();
  const { loginCid, stateCid, level, isLogin } = useAppSelector((state) => state.global);
  const { deviceTableData } = useAppSelector((state) => state.topology);
  const { data, isLoading: loading, isFetching, isSuccess } = useIdentity(level, stateCid, loginCid);
  const { data: users } = useUser(stateCid, level, level === "super_super" || level === "super" || level === "admin");

  const selectProps: SelectProps = {
    mode: "multiple",
    style: { width: "100%" },
    options: selectOption(data?.response?.device_identity),
    maxTagCount: "responsive",
    // value,
    // placeholder: "Select Item...",
    // onChange: (newValue: string[]) => {
    //   setValue(newValue);
    // },
  };

  const disabledDate: RangePickerProps["disabledDate"] = (current) => {
    // Can not select days before today and today
    return current < moment().subtract(1, "days").endOf("day");
  };

  const createTicket = (values: any) => {
    createTicketMutation(values)
      .then(() => {
        setCreateVisible(false);
        form.resetFields();
        message.success(t("ISMS.Success"));
      })
      .catch(() => {
        message.error(t("ISMS.Fail"));
      });
  };

  form.setFieldsValue({
    cid: stateCid,
  });

  const handleChange = (value: any) => {
    setTicketType(value);
  };

  return (
    <Modal
      title={t("ISMS.CreateTicket")}
      visible={CreateVisible}
      className={`${styles.modal}`}
      onCancel={() => {
        setCreateVisible(false);
        setTicketType("");
        form.resetFields();
      }}
      footer={[
        <Button
          style={{ marginRight: 10 }}
          onClick={() => {
            setCreateVisible(false);
            setTicketType("");
            form.resetFields();
          }}
        >
          {t("ISMS.Cancel")}
        </Button>,
        <Button
          key="Submit"
          loading={isLoading}
          type="primary"
          onClick={() => {
            setCreateVisible(false);
            setTicketType("");
            form.submit();
          }}
        >
          {t("ISMS.Submit")}
        </Button>,
      ]}
    >
      <Form layout="vertical" name="CreateInfo" onFinish={createTicket} form={form}>
        <div className="formwrap">
          <Row gutter={24} justify="space-around">
            <Col xs={24} sm={24} md={24} lg={24} xl={10}>
              <h2>{t("ISMS.TicketInfo")}</h2>
              <Divider className={styles.divider} />

              <Form.Item name={"cid"} label={t("ISMS.Cid")} hidden={true}>
                <Input />
              </Form.Item>

              <Form.Item name={"token"} label={t("ISMS.Token")} hidden={true}>
                <Input placeholder={""} maxLength={30} />
              </Form.Item>

              <Form.Item
                name={"ticket_type"}
                label={t("ISMS.TicketType")}
                rules={[{ required: true, message: "Deivce Id is required!" }]}
              >
                <Select
                  style={{ width: "100%" }}
                  placeholder="Select Ticket Type"
                  // defaultValue={["installation"]}
                  onChange={handleChange}
                  optionLabelProp="label"
                >
                  <Option value="installation" label="installation">
                    {t("ISMS.Installation")}
                  </Option>
                  <Option value="inspection" label="inspection">
                    {t("ISMS.Inspection")}
                  </Option>
                </Select>
              </Form.Item>

              <Form.Item name={"description"} label={t("ISMS.Description")}>
                <Input placeholder={""} maxLength={30} />
              </Form.Item>

              {ticketType === "installation" ? (
                <Form.Item
                  name={"new_no"}
                  label={t("ISMS.DeviceQuantity")}
                  // initialValue={0}
                >
                  <InputNumber placeholder={"10"} maxLength={30} style={{ width: "100%" }} min={1} />
                </Form.Item>
              ) : (
                ""
              )}

              {ticketType === "inspection" ? (
                <Form.Item
                  name={"fix_id"}
                  label={t("ISMS.DeviceId")}
                  rules={[{ required: true, message: "Deivce Id is required!" }]}
                >
                  <Select allowClear {...selectProps} placeholder="Select Device ID" />
                </Form.Item>
              ) : (
                ""
              )}

              <Form.Item
                name={"prefix_url"}
                label={t("ISMS.PrefixURL")}
                initialValue="http://192.168.1.1"
                hidden={true}
              >
                <Input placeholder={""} maxLength={30} />
              </Form.Item>

              <Form.Item
                name={"expiration"}
                label={t("ISMS.Expiration")}
                rules={[{ required: true, message: "Expiration date is required!" }]}
                initialValue={moment("23:59:59", "HH:mm:ss")}
              >
                <DatePicker
                  placeholder={""}
                  style={{ width: "100%" }}
                  showToday
                  format="YYYY-MM-DD"
                  disabledDate={disabledDate}
                />
              </Form.Item>
            </Col>

            {/* <Col xs={24} sm={24} md={24} lg={24} xl={10}>
              <h2>{t("ISMS.Scheme")}</h2>
              <Divider className={styles.divider} />

              <Form.Item name={"mail"} rules={[{ type: "email", message: "Invalid!!" }]} label={t("ISMS.Device")}>
                <Input placeholder={t("ISMS.Device")} maxLength={30} />
              </Form.Item>

              <Form.Item
                label={t("ISMS.PeriodAlive(s)")}
                name="period_alive"
                rules={[{ required: true, message: "Required!!" }]}
                initialValue={30}
              >
                <Input
                  style={{ width: "100%" }}
                  min={30}
                  step={30}
                  max={86400}
                  placeholder={t("ISMS.AlivePeriodstepby30")}
                  // onChange={aliveNumberChange}
                />
              </Form.Item>
              <Form.Item
                label={t("ISMS.StatusPeriod(s)")}
                name="period_status"
                rules={[{ required: true, message: "Required!!" }]}
                initialValue={30}
              >
                <InputNumber
                  placeholder={t("ISMS.StatusPeriod")}
                  min={30}
                  step={30}
                  max={86400}
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Form.Item
                label={t("ISMS.IoTPeriod(s)")}
                name="period_iot"
                rules={[{ required: true, message: "Required!!" }]}
                initialValue={30}
              >
                <InputNumber
                  placeholder={t("ISMS.IoTPeriod")}
                  min={30}
                  step={30}
                  max={86400}
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Form.Item
                label={t("ISMS.GPSPeriod(s)")}
                name="period_gps"
                rules={[{ required: true, message: "Required!!" }]}
                initialValue={30}
              >
                <InputNumber
                  placeholder={t("ISMS.GPSPeriod")}
                  min={30}
                  step={30}
                  max={86400}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col> */}
          </Row>
        </div>
      </Form>
    </Modal>
  );
});

export default CreateTicket_Modal;
